import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/synapse/Field';
import { Icon } from '@/components/synapse/Icon';
import { Modal } from '@/components/synapse/Modal';
import { Segmented, type SegmentedOption } from '@/components/synapse/Segmented';
import { DatePicker } from '@/components/synapse/DatePicker';
import { ApiError } from '@/lib/api';
import { translateApiError } from '@/lib/apiError';
import { isNationalIdWellFormed, isPhoneWellFormed } from '@/lib/patientIdentifiers';
import type { Patient, PatientSex, UpdatePatientInput } from '@/types/patients';
import { sexLabelKey } from './patientDisplay';
import { useUpdatePatient } from './usePatients';

interface EditPatientDialogProps {
  patient: Patient;
  onClose: () => void;
  /** Receives the refreshed patient after a successful save. */
  onSaved?: (p: Patient) => void;
}

// The three selectable sexes (a record may carry the legacy `unknown`, which is
// shown as "no selection" until the user picks one). Labels are sourced from the
// central `sexLabelKey` so the wording stays in one place.
const SEX_OPTIONS: SegmentedOption<PatientSex>[] = (['female', 'male', 'other'] as const).map(
  (value) => ({ value, labelKey: sexLabelKey(value) }),
);

/**
 * Edit a patient's registration details (the same fields the quick-create form
 * captures: name, sex, date of birth / age, phone, NIC). Photo editing lives on
 * the chart header's avatar, not here. Opened from the patient summary header and
 * the registry profile; on success the patient query invalidates so both panes
 * refresh. Only changed fields are sent — an unchanged save just closes.
 */
export function EditPatientDialog({ patient, onClose, onSaved }: EditPatientDialogProps) {
  const { t } = useTranslation();
  const update = useUpdatePatient();

  const [fullName, setFullName] = useState(patient.fullName);
  const [sex, setSex] = useState<PatientSex>(patient.sex);
  const [dob, setDob] = useState<string | null>(patient.dateOfBirth);
  const [age, setAge] = useState('');
  const [phone, setPhone] = useState(patient.phone ?? '');
  const [nationalId, setNationalId] = useState(patient.nationalId ?? '');
  const [nameError, setNameError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [nicError, setNicError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // An age is only meaningful as a DOB fallback (mirrors quick-create): when a DOB
  // is set the age field is disabled, and an entered age derives an approximate
  // 1-January date.
  const usingApproxDob = dob == null && age.trim() !== '';

  // Resolve the effective DOB: an explicit DOB wins; otherwise derive an
  // approximate 1-January date from a valid age.
  function effectiveDob(): string | null {
    if (dob != null) return dob;
    const trimmed = age.trim();
    if (trimmed === '') return null;
    const ageNum = Number(trimmed);
    if (Number.isFinite(ageNum) && ageNum >= 0 && ageNum <= 150) {
      const year = new Date().getUTCFullYear() - Math.floor(ageNum);
      return `${String(year).padStart(4, '0')}-01-01`;
    }
    return null;
  }

  // Diff the form against the loaded patient: only changed fields go in the patch.
  // Cleared phone/NIC become `null` (the update endpoint accepts null to clear).
  function buildPatch(): UpdatePatientInput {
    const patch: UpdatePatientInput = {};
    const name = fullName.trim();
    if (name && name !== patient.fullName) patch.fullName = name;
    if (sex !== patient.sex) patch.sex = sex;
    const nextDob = effectiveDob();
    if (nextDob !== patient.dateOfBirth) patch.dateOfBirth = nextDob;
    const nextPhone = phone.trim() || null;
    if (nextPhone !== patient.phone) patch.phone = nextPhone;
    const nextNic = nationalId.trim() || null;
    if (nextNic !== patient.nationalId) patch.nationalId = nextNic;
    return patch;
  }

  const patch = buildPatch();
  const dirty = Object.keys(patch).length > 0;
  const submitting = update.isPending;

  async function handleSave() {
    setSubmitError(null);
    if (!fullName.trim()) {
      setNameError(t('patients.quickCreate.fullNameRequired'));
      return;
    }
    setNameError(null);

    // Check phone and NIC against the same rules the server enforces (shared via
    // @synapse/patient-identifiers) so a rejection lands on the offending field
    // instead of arriving as a generic validation failure. Empty is allowed for
    // both — it clears the field.
    const trimmedPhone = phone.trim();
    const badPhone = trimmedPhone !== '' && !isPhoneWellFormed(trimmedPhone);
    setPhoneError(badPhone ? t('patients.quickCreate.phoneInvalid') : null);

    const trimmedNic = nationalId.trim();
    const badNic = trimmedNic !== '' && !isNationalIdWellFormed(trimmedNic);
    setNicError(badNic ? t('patients.quickCreate.nationalIdInvalid') : null);

    if (badPhone || badNic) return;
    // Nothing changed — closing is the honest no-op (the endpoint would 400 on an
    // empty patch).
    if (!dirty) {
      onClose();
      return;
    }
    try {
      const updated = await update.mutateAsync({ id: patient.id, patch });
      onSaved?.(updated);
      onClose();
    } catch (err) {
      setSubmitError(
        err instanceof ApiError ? translateApiError(err) : t('patients.editDialog.saveFailed'),
      );
    }
  }

  return (
    <Modal
      title={t('patients.editDetails')}
      description={t('patients.editDialog.subtitle')}
      onClose={onClose}
      isPending={submitting}
      footer={
        <>
          <Button variant="secondary" full onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button full onClick={handleSave} disabled={submitting || !fullName.trim()}>
            {submitting ? t('patients.editDialog.saving') : t('patients.editDialog.save')}
          </Button>
        </>
      }
    >
      <Field label={t('patients.quickCreate.fullName')} error={nameError}>
        {(id) => (
          <div className="relative flex items-center">
            <Input
              id={id}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              autoFocus
              className="pr-9"
            />
            <Icon name="user" size={15} className="pointer-events-none absolute right-3 text-muted-2" />
          </div>
        )}
      </Field>

      <Field label={t('patients.quickCreate.sex')}>
        {() => (
          <Segmented
            value={sex}
            onChange={setSex}
            options={SEX_OPTIONS}
            ariaLabel={t('patients.quickCreate.sex')}
          />
        )}
      </Field>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label={t('patients.quickCreate.dateOfBirth')}>
          {(id) => <DatePicker id={id} value={dob} onChange={setDob} clearable max={todayMax()} />}
        </Field>
        <Field
          label={t('patients.quickCreate.age')}
          optional
          hint={usingApproxDob ? t('patients.quickCreate.ageApproxHint') : t('patients.quickCreate.ageHint')}
        >
          {(id) => (
            <div className="relative flex items-center">
              <Input
                id={id}
                type="number"
                inputMode="numeric"
                min={0}
                max={150}
                value={age}
                onChange={(e) => setAge(e.target.value)}
                disabled={dob != null}
                className="pr-12"
              />
              <span className="pointer-events-none absolute right-3 text-[12px] text-muted-2">
                {t('patients.quickCreate.ageUnit')}
              </span>
            </div>
          )}
        </Field>
      </div>

      <Field label={t('patients.quickCreate.phone')} error={phoneError}>
        {(id) => (
          <Input id={id} value={phone} onChange={(e) => setPhone(e.target.value)} mono inputMode="tel" />
        )}
      </Field>

      <Field
        label={t('patients.quickCreate.nationalId')}
        optional
        hint={t('patients.quickCreate.nationalIdHint')}
        error={nicError}
      >
        {(id) => (
          <Input
            id={id}
            value={nationalId}
            onChange={(e) => setNationalId(e.target.value)}
            placeholder={t('patients.quickCreate.nationalIdPlaceholder')}
            mono
          />
        )}
      </Field>

      {submitError && (
        <p className="text-[12.5px] text-destructive" role="alert">
          {submitError}
        </p>
      )}
    </Modal>
  );
}

function todayMax(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}
